import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, {Fragment} from 'react';
import {Controller, FieldValues, useFormContext} from 'react-hook-form';

import Icon from 'presentation/icons';
import {TextInputProps} from './types';

export function TextInput<T extends FieldValues>({
	name,
	inputRef,
	autoComplete,
	hideHelperText,
	label,
	helperText,
	endAdornmentVariant,
	endAdornmentIcon,
	handleAction,
	handleClearQuery,
	customHelperText,
	...rest
}: TextInputProps<T>) {
	const {resetField, control} = useFormContext<T>();

	function handleClear() {
		resetField(name);
		if (handleClearQuery) {
			handleClearQuery();
		}
	}

	const inputAdornment = (isDirty: boolean) => {
		if (endAdornmentVariant === 'close') {
			return (
				<Fragment>
					{isDirty ? (
						<InputAdornment position="end">
							<IconButton aria-label="clear field" onClick={handleClear} edge="end">
								<Icon name="close-filled" />
							</IconButton>
						</InputAdornment>
					) : null}
				</Fragment>
			);
		}
		return (
			<Fragment>
				{endAdornmentIcon ? (
					<InputAdornment position="end">
						<IconButton onClick={handleAction} edge="end">
							<Icon name={endAdornmentIcon} />
						</IconButton>
					</InputAdornment>
				) : null}
			</Fragment>
		);
	};

	return (
		<Controller
			name={name}
			control={control}
			render={({field, fieldState: {invalid, isDirty, error}}) => {
				const showErrorText = isDirty && !hideHelperText;
				const helperTextVariant =
					customHelperText || (showErrorText ? error?.message : helperText);

				return (
					<TextField
						{...rest}
						{...field}
						autoComplete={autoComplete}
						id={field.name}
						inputRef={inputRef}
						error={isDirty && invalid}
						helperText={helperTextVariant}
						label={label}
						slotProps={{
							...rest.slotProps,
							input: {
								...rest.slotProps?.input,
								endAdornment: inputAdornment(isDirty),
							},
						}}
					/>
				);
			}}
		/>
	);
}
